import { useEffect, useMemo, useState, useCallback } from 'react';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import { useGetApiPnrsQuery, GetApiPnrsApiArg } from '../../app/athenaApiGen';
import { mapPNRApiDataToRows } from './PNRUtils';
import { WorkflowProgressValue } from '../../utils/workflow';
import { setPage, setPageSize, setSearch, setWorkflowFilter } from '../../app/pnr/pnrSlice';
import { RootState } from '../../app/store';

type PnrQueryArg = GetApiPnrsApiArg & {
  workflowProgress?: string;
};

export const usePNRList = () => {
  const dispatch = useDispatch();

  // Get state from Redux instead of local state
  const { page, pageSize, search, workflowFilter } = useSelector(
    (state: RootState) => ({
      page: state.pnr.page,
      pageSize: state.pnr.pageSize,
      search: state.pnr.search,
      workflowFilter: state.pnr.workflowFilter,
    }),
    shallowEqual
  );

  // Debounced search stays as local state (derived value)
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Debounce search input
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 350);
    return () => clearTimeout(t);
  }, [search]);

  // Fetch data
  const queryArgs: PnrQueryArg = {
    page: page + 1,
    pageSize: pageSize,
    search: debouncedSearch || undefined,
    currentVersion: true,
  };

  if (workflowFilter !== 'all') {
    queryArgs.workflowProgress = workflowFilter;
  }

  const { data, isLoading, isFetching, isError, refetch } = useGetApiPnrsQuery(queryArgs, {
    refetchOnMountOrArgChange: true,
  });

  // Transform data
  const mappedRows = useMemo(() => {
    const items = data?.pnrs || [];
    return mapPNRApiDataToRows(items);
  }, [data]);

  const rows = useMemo(() => {
    if (workflowFilter === 'all') return mappedRows;
    return mappedRows.filter((row) => row.workflowProgress === workflowFilter);
  }, [mappedRows, workflowFilter]);

  const rowCount =
    workflowFilter === 'all'
      ? data?.totalCount ?? 0
      : data?.totalCount ?? rows.length;
  const loading = isLoading || isFetching;

  const quickFilterCounts = useMemo(() => {
    const base = {
      all: data?.totalCount ?? mappedRows.length,
      draft: 0,
      submitted: 0,
      completed: 0,
    };
    mappedRows.forEach((row) => {
      if (row.workflowProgress === 'draft') base.draft += 1;
      if (row.workflowProgress === 'submitted') base.submitted += 1;
      if (row.workflowProgress === 'completed') base.completed += 1;
    });
    return base;
  }, [data?.totalCount, mappedRows]);

  // Dispatch Redux actions instead of setState
  const handlePaginationModelChange = useCallback((model: { page: number; pageSize: number }) => {
    dispatch(setPage(model.page));
    dispatch(setPageSize(model.pageSize));
  }, [dispatch]);

  const handleSearchChange = useCallback((value: string) => {
    dispatch(setSearch(value));
  }, [dispatch]);

  const handleWorkflowFilterChange = useCallback((value: WorkflowProgressValue | 'all') => {
    dispatch(setWorkflowFilter(value));
  }, [dispatch]);

  return {
    paginationModel: { page, pageSize },
    setPaginationModel: handlePaginationModelChange,
    search,
    setSearch: handleSearchChange,
    workflowFilter,
    setWorkflowFilter: handleWorkflowFilterChange,
    rows,
    rowCount,
    loading,
    isError,
    refetch,
    quickFilterCounts,
  };
};
